import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui';
import { CANCEL_REASONS, TIERS } from '@/constants/billing';
import type { SubscriptionTier } from '@/types';
import { useRegisterOverlay } from '@/stores/overlayStore';

export interface DowngradeReasonModalProps {
  open: boolean;
  /** The plan the user is currently on. Drives the title copy. */
  currentTier: SubscriptionTier;
  /**
   * The plan they're moving to. Null when the modal is closed; the
   * parent passes the chosen target when opening.
   */
  targetTier: SubscriptionTier | null;
  /** ISO timestamp the current plan stays active until. Optional. */
  periodEnd: string | null;
  onClose: () => void;
  /**
   * Called with the selected reason key (and the free-text input when
   * 'other' is selected) when the user confirms. Parent owns the API
   * call and toast.
   */
  onContinue: (
    reasonKey: string,
    otherText: string | null,
  ) => void | Promise<void>;
}

function formatPeriodEnd(iso: string | null): string {
  if (!iso) return 'the end of your billing period';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Bottom-sheet retention modal triggered when the user taps a
 * "Switch to <lower tier>" CTA on the Plan page. Spec rules:
 *
 * - Not dismissible by backdrop tap.
 * - Exit path: "Keep my plan" only — no X icon (Figma, AC 3).
 * - Reason selection resets each time the modal opens.
 * - Continue is disabled until one reason is selected.
 * - 'Other...' is a radio row with an inline text input — the input
 *   is enabled only when 'other' is the active selection.
 */
export function DowngradeReasonModal({
  open,
  currentTier,
  targetTier,
  periodEnd,
  onClose,
  onContinue,
}: DowngradeReasonModalProps) {
  const [reasonKey, setReasonKey] = useState<string | null>(null);
  const [otherText, setOtherText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset selection every time the modal opens, per spec ("Reason
  // selection resets each time the modal opens — does not persist
  // between sessions").
  useEffect(() => {
    if (open) {
      setReasonKey(null);
      setOtherText('');
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Full-screen while open: hide and pause the bottom toast slot (YAR-476).
  useRegisterOverlay(open);

  if (!open) return null;

  const continueDisabled = reasonKey === null || submitting;

  const handleContinue = async () => {
    if (continueDisabled || !reasonKey) return;
    setSubmitting(true);
    try {
      const other = reasonKey === 'other' ? otherText.trim() || null : null;
      await onContinue(reasonKey, other);
    } finally {
      setSubmitting(false);
    }
  };

  const subtitle =
    targetTier && periodEnd
      ? `Your ${TIERS[currentTier].label} plan stays active until ${formatPeriodEnd(periodEnd)}.`
      : `Your ${TIERS[currentTier].label} plan stays active until the end of your billing period.`;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center px-5"
      role="presentation"
    >
      {/* Backdrop — intentionally not clickable per spec. */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[5px]"
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="downgrade-reason-title"
        className="relative w-full max-w-md rounded-xl border border-border bg-background p-5 shadow-[0px_0px_16px_-4px_rgba(56,64,74,0.1)]"
      >
        <div className="flex flex-col items-center gap-1.5 text-center">
          <h2
            id="downgrade-reason-title"
            className="text-xl font-medium text-foreground"
          >
            Before you go, what's the reason for switching?
          </h2>
          <p className="text-base text-foreground">{subtitle}</p>
        </div>

        <fieldset className="mt-4 flex flex-col gap-3">
          <legend className="sr-only">Reason for switching plans</legend>
          {CANCEL_REASONS.map((option) => {
            const checked = reasonKey === option.key;
            const isOther = option.key === 'other';
            return (
              <label
                key={option.key}
                className="flex items-center gap-3 cursor-pointer"
              >
                <input
                  type="radio"
                  name="downgrade-reason"
                  value={option.key}
                  checked={checked}
                  onChange={() => setReasonKey(option.key)}
                  className="size-5 cursor-pointer accent-primary"
                />
                {isOther ? (
                  <input
                    type="text"
                    placeholder="Other..."
                    value={otherText}
                    onChange={(e) => {
                      setOtherText(e.target.value);
                      // Tapping into the input implies the 'other'
                      // selection — this matches the Figma where the
                      // text field doubles as the radio label.
                      if (reasonKey !== 'other') setReasonKey('other');
                    }}
                    aria-label="Other reason"
                    className="flex-1 h-10 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                  />
                ) : (
                  <span className="text-base text-foreground">
                    {option.label}
                  </span>
                )}
              </label>
            );
          })}
        </fieldset>

        <div className="mt-4 flex flex-col gap-3">
          <Button
            variant="primary"
            fullWidth
            onClick={handleContinue}
            disabled={continueDisabled}
            loading={submitting}
          >
            Continue
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="w-full px-2 py-1.5 text-base font-semibold text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            Keep my plan
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
